import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

// Public Pages
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AITransparencyPage from "./pages/AITransparencyPage";
import BrandedLoginPage from "./pages/BrandedLoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Dashboard Pages
import StudentDashboard from "./pages/student/StudentDashboard";
import StudentResults from "./pages/student/StudentResults";
import AgentDashboard from "./pages/agent/AgentDashboard";
import AgentApplications from "./pages/agent/AgentApplications";
import AgentInterviews from "./pages/agent/AgentInterviews";
import AgentBilling from "./pages/agent/AgentBilling";
import OrganizationDashboard from "./pages/organization/OrganizationDashboard";
import OrganizationApplications from "./pages/organization/OrganizationApplications";
import OrganizationQuestions from "./pages/organization/OrganizationQuestions";
import OrganizationProfile from "./pages/organization/OrganizationProfile";
import OrganizationAgents from "./pages/organization/OrganizationAgents";
import OrganizationInterviews from "./pages/organization/OrganizationInterviews";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUniversities from "./pages/admin/AdminUniversities";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminQuestions from "./pages/admin/AdminQuestions";
import AdminInterviews from "./pages/admin/AdminInterviews";
import AdminApplications from "./pages/admin/AdminApplications";
import AdminRoles from "./pages/admin/AdminRoles";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminMembers from "./pages/admin/AdminMembers";
import AdminSupport from "./pages/admin/AdminSupport";

// V2 Engine Pages
import V2Dashboard from "./pages/admin/v2/V2Dashboard";
import V2Questions from "./pages/admin/v2/V2Questions";
import V2Categories from "./pages/admin/v2/V2Categories";
import V2Settings from "./pages/admin/v2/V2Settings";
import V2CreateInterview from "./pages/admin/v2/V2CreateInterview";
import V2TestHistory from "./pages/admin/v2/V2TestHistory";
import V2InterviewRoom from "./pages/admin/v2/V2InterviewRoom";
import V2InterviewDetail from "./pages/admin/v2/V2InterviewDetail";

// Support Pages
import SupportTickets from "./pages/support/SupportTickets";
import HelpCenter from "./pages/support/HelpCenter";

// Interview Pages
import InterviewVerification from "./pages/interview/InterviewVerification";
import InterviewRoom from "./pages/interview/InterviewRoom";

// Payment Pages
import PaymentSuccess from "./pages/payment/PaymentSuccess";
import PaymentCancel from "./pages/payment/PaymentCancel";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Dashboard Redirect based on role
const DashboardRedirect = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  switch (user.role) {
    case 'super_admin':
      return <Navigate to="/admin" replace />;
    case 'agent':
      return <Navigate to="/agent" replace />;
    case 'organization':
      return <Navigate to="/organization" replace />;
    default:
      return <Navigate to="/student" replace />;
  }
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/org-login/:orgSlug" element={<BrandedLoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/ai-transparency" element={<AITransparencyPage />} />
            
            {/* Dashboard Redirect */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />
            
            {/* Public Student Results Page */}
            <Route path="/results/:resultToken" element={<StudentResults />} />
            
            {/* Student Routes */}
            <Route path="/student" element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            } />
            
            {/* Agent Routes */}
            <Route path="/agent" element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentDashboard />
              </ProtectedRoute>
            } />
            
            {/* Organization Routes */}
            <Route path="/organization" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationDashboard />
              </ProtectedRoute>
            } />
            <Route path="/organization/applications" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationApplications />
              </ProtectedRoute>
            } />
            <Route path="/organization/questions" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationQuestions />
              </ProtectedRoute>
            } />
            <Route path="/organization/agents" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationAgents />
              </ProtectedRoute>
            } />
            <Route path="/organization/team" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationAgents />
              </ProtectedRoute>
            } />
            <Route path="/organization/interviews" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationInterviews />
              </ProtectedRoute>
            } />
            <Route path="/organization/billing" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationDashboard initialTab="billing" />
              </ProtectedRoute>
            } />
            <Route path="/organization/profile" element={
              <ProtectedRoute allowedRoles={['organization']}>
                <OrganizationProfile />
              </ProtectedRoute>
            } />
            
            {/* Agent Routes */}
            <Route path="/agent" element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentDashboard />
              </ProtectedRoute>
            } />
            <Route path="/agent/partner-agents" element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentDashboard />
              </ProtectedRoute>
            } />
            <Route path="/agent/interviews" element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentInterviews />
              </ProtectedRoute>
            } />
            <Route path="/agent/applications" element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentApplications />
              </ProtectedRoute>
            } />
            <Route path="/agent/billing" element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentBilling />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="/admin/universities" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminUniversities />
              </ProtectedRoute>
            } />
            <Route path="/admin/packages" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminPackages />
              </ProtectedRoute>
            } />
            <Route path="/admin/questions" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminQuestions />
              </ProtectedRoute>
            } />
            <Route path="/admin/interviews" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminInterviews />
              </ProtectedRoute>
            } />
            <Route path="/admin/applications" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminApplications />
              </ProtectedRoute>
            } />
            <Route path="/admin/roles" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminRoles />
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminSettings />
              </ProtectedRoute>
            } />
            <Route path="/admin/notifications" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminNotifications />
              </ProtectedRoute>
            } />
            <Route path="/admin/members" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminMembers />
              </ProtectedRoute>
            } />
            <Route path="/admin/support" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminSupport />
              </ProtectedRoute>
            } />
            <Route path="/admin/support/:ticketId" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AdminSupport />
              </ProtectedRoute>
            } />
            
            {/* V2 Engine Routes — Super Admin only */}
            <Route path="/admin/v2" element={<ProtectedRoute allowedRoles={['super_admin']}><V2Dashboard /></ProtectedRoute>} />
            <Route path="/admin/v2/questions" element={<ProtectedRoute allowedRoles={['super_admin']}><V2Questions /></ProtectedRoute>} />
            <Route path="/admin/v2/categories" element={<ProtectedRoute allowedRoles={['super_admin']}><V2Categories /></ProtectedRoute>} />
            <Route path="/admin/v2/settings" element={<ProtectedRoute allowedRoles={['super_admin']}><V2Settings /></ProtectedRoute>} />
            <Route path="/admin/v2/create" element={<ProtectedRoute allowedRoles={['super_admin']}><V2CreateInterview /></ProtectedRoute>} />
            <Route path="/admin/v2/interviews/:id" element={<ProtectedRoute allowedRoles={['super_admin']}><V2InterviewDetail /></ProtectedRoute>} />
            <Route path="/admin/v2/history" element={<ProtectedRoute allowedRoles={['super_admin']}><V2TestHistory /></ProtectedRoute>} />

            {/* V2 Interview Room — public (accessed via secure token) */}
            <Route path="/interview/v2/:token" element={<V2InterviewRoom />} />

            {/* Support Routes (for agents and organizations) */}
            <Route path="/support" element={
              <ProtectedRoute allowedRoles={['agent', 'organization']}>
                <SupportTickets />
              </ProtectedRoute>
            } />
            <Route path="/support/:ticketId" element={
              <ProtectedRoute allowedRoles={['agent', 'organization']}>
                <SupportTickets />
              </ProtectedRoute>
            } />
            <Route path="/help" element={
              <ProtectedRoute allowedRoles={['agent', 'organization', 'super_admin']}>
                <HelpCenter />
              </ProtectedRoute>
            } />
            
            {/* Interview Routes (Public with token) */}
            <Route path="/interview/:token" element={<InterviewVerification />} />
            <Route path="/interview/:token/room" element={<InterviewRoom />} />
            
            {/* Payment Routes */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            
            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            className: 'font-body',
          }}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
